/**
 * The design-system half of a storefront theme: the optional groups `lootThemeSchema` carries
 * beside the shadcn tokens (docs/design-system/spec.md, section 4).
 *
 * Every field is a choice from a fixed set or a color, never free CSS, so a tenant (or an agent
 * proposing a theme) can only pick among designed options. `resolveThemeSpec` always returns a
 * complete, sanitized set, the same guarantee `resolveStorefrontThemeVars` gives the tokens:
 * it loops over our keys, never the stored object, so a tenant can never name a variable.
 *
 * Its own module so the design system can read it without importing index back.
 */
import { z } from "zod";
import { safeThemeColor } from "./storefrontThemeVars.js";
import type { StorefrontTokenKey } from "./storefrontTokens.js";

/*
 * Semantic color roles are named as the CSS variable after `--color-`, which is the Figma
 * variable path with `/` as `-` (`color/bg/surface` → `bg-surface`).
 */

/**
 * The roles that are another name for a contract token: the role's color is the token's. A theme
 * sets them through `tokens`, never `semantic`, so everything that reads a theme (the storefront,
 * the opener, the cookie banner, emails, Shopify) keeps one color for each.
 */
export const TOKEN_BACKED_ROLES = {
  "bg-canvas": "background",
  "bg-surface": "card",
  "bg-raised": "popover",
  "bg-hover": "accent",
  "border-subtle": "border",
  "text-primary": "foreground",
  "text-secondary": "muted-foreground",
  "text-on-accent": "primary-foreground",
  "text-on-danger": "destructive-foreground",
  "accent-default": "primary",
  "focus-ring": "ring",
  "status-danger": "destructive",
} as const satisfies Record<string, StorefrontTokenKey>;
export type TokenBackedRole = keyof typeof TOKEN_BACKED_ROLES;

/**
 * The semantic color roles a theme may set directly: those no contract token names. Each one
 * otherwise derives from the tokens in the design system's theme.css.
 */
export const SEMANTIC_COLOR_KEYS = [
  "bg-inset",
  "bg-pressed",
  "border-default",
  "border-strong",
  "border-input",
  "text-tertiary",
  "text-disabled",
  "accent-hover",
  "accent-pressed",
  "accent-subtle",
  "accent-text",
  "accent-highlight",
  "status-success",
  "status-success-subtle",
  "status-warning",
  "status-warning-subtle",
  "status-danger-subtle",
  "state-hover",
  "state-pressed",
  "glass-fill",
  "glass-edge",
  "glass-active",
  "brand-start",
  "brand-end",
  "rarity-chase",
] as const;
export type SemanticColorKey = (typeof SEMANTIC_COLOR_KEYS)[number];

/** Every semantic color role: set through its token, or directly. */
export const SEMANTIC_ROLES = [
  ...(Object.keys(TOKEN_BACKED_ROLES) as TokenBackedRole[]),
  ...SEMANTIC_COLOR_KEYS,
] as const;
export type SemanticRole = TokenBackedRole | SemanticColorKey;

/** At most this many stage colors; a stage recipe reads them in order. */
export const MAX_STAGE_COLORS = 4;

export const DISPLAY_WEIGHTS = [500, 600, 700, 800] as const;
export const DISPLAY_TRACKINGS = ["tight", "normal"] as const;
export const DISPLAY_CASES = ["none", "upper"] as const;
export const CONTROL_SHAPES = ["pill", "rounded"] as const;
export const DENSITIES = ["compact", "default", "roomy"] as const;
export const SURFACE_STYLES = ["flat", "soft"] as const;
export const GLASS_STRENGTHS = [0, 1, 2, 3] as const;
export const GLOW_INTENSITIES = [0, 1, 2, 3] as const;
export const MOTION_PRESETS = ["calm", "standard", "lively"] as const;
export const STAGE_RECIPES = ["none", "glow", "water", "aurora"] as const;
/** The icon pack's drawing styles (`@loot-os/icons`). */
export const ICON_STYLES = ["rounded", "sharp", "outline", "duotone"] as const;
/**
 * How much the store's icons animate: `none`; `events`, an icon plays when something happens to
 * the shopper (a notification arrives, a favorite, a balance change); `all`, events plus hovering
 * an icon-only control or a nav item.
 */
export const ICON_MOTIONS = ["none", "events", "all"] as const;
export const HEADER_STYLES = ["attached", "floating"] as const;
export const CHROME_STYLES = ["solid", "glass"] as const;
/** `stream`: one line per message, name and text inline. `card`: a filled block per message. */
export const CHAT_ROW_STYLES = ["card", "stream"] as const;
/** `text`: the header's links are words alone. `icon`: each leads with its icon. */
export const NAV_LINK_STYLES = ["text", "icon"] as const;
/**
 * `plain`: the footer opens with its columns. `tear`: a sealed pack's strip runs across its top,
 * which a shopper rips to reveal a sample card from the store's boxes (nothing is opened).
 */
export const FOOTER_STYLES = ["plain", "tear"] as const;

/** The groups `lootThemeSchema` gains. All optional, so every stored theme still parses. */
export const themeSpecShape = {
  /**
   * Direct values for semantic roles, overriding what theme.css derives. Every role name parses,
   * so a stored theme never fails on one; a token-backed role is ignored on read and refused on
   * save (`refuseUnacceptedThemeColors`), with the token to set instead.
   */
  semantic: z.partialRecord(z.enum(SEMANTIC_ROLES), z.string()).optional(),
  /** The font stack for money, odds and anything a collector compares. */
  monoFontFamily: z.string().optional(),
  type: z
    .object({
      displayWeight: z.literal(DISPLAY_WEIGHTS).optional(),
      displayTracking: z.enum(DISPLAY_TRACKINGS).optional(),
      displayCase: z.enum(DISPLAY_CASES).optional(),
    })
    .optional(),
  shape: z
    .object({
      controls: z.enum(CONTROL_SHAPES).optional(),
      density: z.enum(DENSITIES).optional(),
    })
    .optional(),
  surface: z
    .object({
      style: z.enum(SURFACE_STYLES).optional(),
      /** 0 renders glass roles solid. */
      glass: z.literal(GLASS_STRENGTHS).optional(),
    })
    .optional(),
  glow: z.object({ intensity: z.literal(GLOW_INTENSITIES).optional() }).optional(),
  motion: z.object({ preset: z.enum(MOTION_PRESETS).optional() }).optional(),
  /** Unset `motion` follows the motion preset: `all` when lively, else `events`. */
  icons: z
    .object({
      style: z.enum(ICON_STYLES).optional(),
      motion: z.enum(ICON_MOTIONS).optional(),
    })
    .optional(),
  stage: z
    .object({
      recipe: z.enum(STAGE_RECIPES).optional(),
      colors: z.array(z.string()).max(MAX_STAGE_COLORS).optional(),
    })
    .optional(),
  layout: z
    .object({
      header: z.enum(HEADER_STYLES).optional(),
      chrome: z.enum(CHROME_STYLES).optional(),
      chatRows: z.enum(CHAT_ROW_STYLES).optional(),
      navLinks: z.enum(NAV_LINK_STYLES).optional(),
      footer: z.enum(FOOTER_STYLES).optional(),
    })
    .optional(),
};

const themeSpecSchema = z.object(themeSpecShape);
/** The stored design-system groups of a theme, as `lootThemeSchema` parses them. */
export type ThemeSpecFields = z.infer<typeof themeSpecSchema>;

/** Every choice made, and every color either sanitized or absent. */
export interface ResolvedThemeSpec {
  /** Only the roles set and accepted; the rest derive in CSS. */
  semantic: Partial<Record<SemanticColorKey, string>>;
  monoFontFamily: string | undefined;
  type: {
    displayWeight: (typeof DISPLAY_WEIGHTS)[number];
    displayTracking: (typeof DISPLAY_TRACKINGS)[number];
    displayCase: (typeof DISPLAY_CASES)[number];
  };
  shape: { controls: (typeof CONTROL_SHAPES)[number]; density: (typeof DENSITIES)[number] };
  surface: { style: (typeof SURFACE_STYLES)[number]; glass: (typeof GLASS_STRENGTHS)[number] };
  glow: { intensity: (typeof GLOW_INTENSITIES)[number] };
  motion: { preset: (typeof MOTION_PRESETS)[number] };
  icons: { style: (typeof ICON_STYLES)[number]; motion: (typeof ICON_MOTIONS)[number] };
  stage: { recipe: (typeof STAGE_RECIPES)[number]; colors: string[] };
  layout: {
    header: (typeof HEADER_STYLES)[number];
    chrome: (typeof CHROME_STYLES)[number];
    chatRows: (typeof CHAT_ROW_STYLES)[number];
    navLinks: (typeof NAV_LINK_STYLES)[number];
    footer: (typeof FOOTER_STYLES)[number];
  };
}

/** What a theme that sets nothing gets: today's storefront, unchanged in feel. */
export const THEME_SPEC_DEFAULTS: Omit<ResolvedThemeSpec, "semantic" | "monoFontFamily"> = {
  type: { displayWeight: 700, displayTracking: "tight", displayCase: "none" },
  shape: { controls: "rounded", density: "default" },
  surface: { style: "flat", glass: 0 },
  glow: { intensity: 1 },
  motion: { preset: "standard" },
  icons: { style: "rounded", motion: "events" },
  stage: { recipe: "none", colors: [] },
  layout: {
    header: "attached",
    chrome: "solid",
    chatRows: "card",
    navLinks: "text",
    footer: "plain",
  },
};

/** `value` when it is one of `options`, else `fallback`. Stored data is never trusted. */
function pick<T extends string | number>(options: readonly T[], value: unknown, fallback: T): T {
  return options.includes(value as T) ? (value as T) : fallback;
}

/**
 * A stored theme's design-system groups, complete and sanitized. Accepts anything (a theme saved
 * before a group existed, or one that skipped validation) and never throws.
 */
export function resolveThemeSpec(theme?: ThemeSpecFields | null): ResolvedThemeSpec {
  const d = THEME_SPEC_DEFAULTS;
  const semantic: Partial<Record<SemanticColorKey, string>> = {};
  for (const key of SEMANTIC_COLOR_KEYS) {
    const safe = safeThemeColor(theme?.semantic?.[key]);
    if (safe) semantic[key] = safe;
  }
  const storedColors = theme?.stage?.colors;
  const stageColors = (Array.isArray(storedColors) ? storedColors : [])
    .slice(0, MAX_STAGE_COLORS)
    .map((color) => safeThemeColor(color))
    .filter((color) => color !== null);
  const motionPreset = pick(MOTION_PRESETS, theme?.motion?.preset, d.motion.preset);

  return {
    semantic,
    monoFontFamily: theme?.monoFontFamily,
    type: {
      displayWeight: pick(DISPLAY_WEIGHTS, theme?.type?.displayWeight, d.type.displayWeight),
      displayTracking: pick(
        DISPLAY_TRACKINGS,
        theme?.type?.displayTracking,
        d.type.displayTracking,
      ),
      displayCase: pick(DISPLAY_CASES, theme?.type?.displayCase, d.type.displayCase),
    },
    shape: {
      controls: pick(CONTROL_SHAPES, theme?.shape?.controls, d.shape.controls),
      density: pick(DENSITIES, theme?.shape?.density, d.shape.density),
    },
    surface: {
      style: pick(SURFACE_STYLES, theme?.surface?.style, d.surface.style),
      glass: pick(GLASS_STRENGTHS, theme?.surface?.glass, d.surface.glass),
    },
    glow: { intensity: pick(GLOW_INTENSITIES, theme?.glow?.intensity, d.glow.intensity) },
    motion: { preset: motionPreset },
    icons: {
      style: pick(ICON_STYLES, theme?.icons?.style, d.icons.style),
      motion: pick(
        ICON_MOTIONS,
        theme?.icons?.motion,
        motionPreset === "lively" ? "all" : d.icons.motion,
      ),
    },
    stage: {
      recipe: pick(STAGE_RECIPES, theme?.stage?.recipe, d.stage.recipe),
      colors: stageColors,
    },
    layout: {
      header: pick(HEADER_STYLES, theme?.layout?.header, d.layout.header),
      chrome: pick(CHROME_STYLES, theme?.layout?.chrome, d.layout.chrome),
      chatRows: pick(CHAT_ROW_STYLES, theme?.layout?.chatRows, d.layout.chatRows),
      navLinks: pick(NAV_LINK_STYLES, theme?.layout?.navLinks, d.layout.navLinks),
      footer: pick(FOOTER_STYLES, theme?.layout?.footer, d.layout.footer),
    },
  };
}
